export interface SummaryData {
    readonly summary_json: string;
    readonly summary_markdown: string;
    readonly time_range: string;
    readonly message_count: number;
    readonly range_display: string;
    readonly requester_id?: string;
}

export interface SummaryCoverage {
    readonly limitedToMembership: boolean;
    readonly endExclusive: boolean;
    readonly unavailableEncryptedEvents: number;
}

export interface SummaryPresentation {
    readonly data: SummaryData;
    readonly requestEventId?: string;
    readonly coverage?: SummaryCoverage;
}

export interface SummaryDocument {
    readonly overallSummary: string;
    readonly keyPoints: readonly string[];
    readonly topics: readonly {readonly topic: string; readonly details: string}[];
    readonly decisions: readonly {readonly decision: string; readonly participants: string}[];
    readonly actionItems: readonly {readonly task: string; readonly assignee: string | null}[];
    readonly announcements: readonly string[];
}

const MAX_TEXT_LENGTH = 65_536;
const MAX_ITEMS = 128;
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string' && value.length <= MAX_TEXT_LENGTH;
const isIdentity = (value: unknown): value is string => isText(value) && value.length > 0 && value.length <= 512;
const isTimestamp = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value);

export function parseSummaryPresentation(content: unknown): SummaryPresentation | undefined {
    if (!isRecord(content)) return undefined;
    const data = content['org.phonehq.summary_data'] ?? content['m.summary_data'];
    if (!isRecord(data) || !isText(data.summary_json) || !isText(data.summary_markdown)
        || !isText(data.time_range) || !isText(data.range_display)
        || typeof data.message_count !== 'number' || !Number.isSafeInteger(data.message_count)
        || data.message_count < 1 || data.message_count > 10_000
        || (data.requester_id !== undefined && !isIdentity(data.requester_id))) return undefined;
    const relation = content['m.relates_to'];
    if (relation !== undefined && (!isRecord(relation)
        || (relation.event_id !== undefined && !isIdentity(relation.event_id)))) return undefined;
    const requestEventId = isRecord(relation) && isIdentity(relation.event_id) ? relation.event_id : undefined;
    const metadata = content['org.phonehq.ai_result'];
    let coverage: SummaryCoverage | undefined;
    if (metadata !== undefined) {
        if (!isRecord(metadata) || metadata.schema_version !== 1 || !isRecord(metadata.coverage)) return undefined;
        const value = metadata.coverage;
        if (!isTimestamp(value.requested_start_ms) || !isTimestamp(value.start_ms) || !isTimestamp(value.end_ms)
            || value.requested_start_ms > value.start_ms || value.start_ms >= value.end_ms
            || value.end_exclusive !== true || !isText(value.timezone) || !value.timezone
            || value.limited_to_membership !== (value.start_ms > value.requested_start_ms)) return undefined;
        const unavailableEncryptedEvents = value.unavailable_encrypted_events ?? 0;
        if (typeof unavailableEncryptedEvents !== 'number' || !Number.isSafeInteger(unavailableEncryptedEvents)
            || unavailableEncryptedEvents < 0 || unavailableEncryptedEvents > 10_000) return undefined;
        coverage = {limitedToMembership: value.limited_to_membership, endExclusive: true, unavailableEncryptedEvents};
    }
    return {data: {
        summary_json: data.summary_json, summary_markdown: data.summary_markdown,
        time_range: data.time_range, message_count: data.message_count, range_display: data.range_display,
        requester_id: data.requester_id,
    }, requestEventId, coverage};
}

function readItems<T>(value: unknown, parse: (item: unknown) => T | undefined): readonly T[] | undefined {
    if (value === undefined) return [];
    if (!Array.isArray(value) || value.length > MAX_ITEMS) return undefined;
    const items: T[] = [];
    for (const item of value) {
        const parsed = parse(item);
        if (parsed === undefined) return undefined;
        items.push(parsed);
    }
    return items;
}

export function parseSummaryDocument(serialized: string): SummaryDocument | undefined {
    if (serialized.length > MAX_TEXT_LENGTH) return undefined;
    let value: unknown;
    try { value = JSON.parse(serialized); } catch { return undefined; }
    if (!isRecord(value)) return undefined;
    const overallSummary = value.overall_summary ?? value.summary;
    if (!isText(overallSummary) || !overallSummary.trim()) return undefined;
    const text = (item: unknown) => isText(item) ? item : undefined;
    const keyPoints = readItems(value.key_points, text);
    const announcements = readItems(value.announcements, text);
    const topics = readItems(value.topics, item => isRecord(item) && isText(item.topic) && isText(item.details)
        ? {topic: item.topic, details: item.details} : undefined);
    const decisions = readItems(value.decisions, item => isRecord(item) && isText(item.decision) && isText(item.participants)
        ? {decision: item.decision, participants: item.participants} : undefined);
    const actionItems = readItems(value.action_items, item => isRecord(item) && isText(item.task)
        && (item.assignee === null || isText(item.assignee)) ? {task: item.task, assignee: item.assignee} : undefined);
    if (!keyPoints || !announcements || !topics || !decisions || !actionItems) return undefined;
    return {overallSummary, keyPoints, announcements, topics, decisions, actionItems};
}
